// Heart Games Shared Styles
// Common styled components for Heart Games

import styled from 'styled-components';
import { theme } from '../theme';

// Container Components
export const HeartGamesContainer = styled.div`
  background: ${theme.colors.background.primary}; /* Light background matching HeartGamesHub */
  min-height: 100vh;
  width: 100%;
  padding: 0;
  padding-bottom: 80px; /* Space for bottom navigation on mobile */
  /* Add top padding to account for fixed navigation */
  padding-top: 20px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-bottom: 100px;
    padding-top: 20px;
  }
`;

export const HeartGamesHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  padding-top: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    padding-top: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.lg};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    padding-top: ${theme.spacing.sm};
    margin-bottom: ${theme.spacing.md};
  }
`;

export const HeartGamesTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }

  @media (max-width: 400px) {
    font-size: 1.1rem;
    gap: ${theme.spacing.xs};
  }

  @media (max-width: 320px) {
    font-size: 1rem;
  }
`;

export const HeartGamesSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  margin-top: ${theme.spacing.xs};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
  }
`;

// Progress Components
export const GameProgressContainer = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

export const ProgressSection = styled.div`
  margin-top: ${theme.spacing.md};
`;

export const ProgressBar = styled.div<{ value: number }>`
  width: 100%;
  height: 8px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.value}%;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee4d4d 100%);
    border-radius: ${theme.borderRadius.sm};
    transition: width 0.3s ease;
  }
`;

export const ProgressText = styled.span`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

export const ProgressPercentage = styled.span`
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
  font-weight: 600;
  margin-left: auto;
`;

// Button Components
export const HeartGamesButton = styled.button<{ variant?: 'primary' | 'secondary' | 'outline' }>`
  background: ${props => {
    switch (props.variant) {
      case 'primary':
        return 'linear-gradient(135deg, #ff8c42 0%, #ff6b35 100%)';
      case 'secondary':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'outline':
        return 'transparent';
      default:
        return 'linear-gradient(135deg, #ff8c42 0%, #ff6b35 100%)';
    }
  }};
  
  color: ${props => props.variant === 'outline' ? theme.colors.text.primary : 'white'};
  border: ${props => props.variant === 'outline' ? `2px solid ${theme.colors.border.medium}` : 'none'};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.medium};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md} ${theme.spacing.lg};
    min-height: 48px;
    font-size: 1rem;
  }

  @media (max-width: 320px) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    min-height: 40px;
    font-size: 0.9rem;
  }
`;

export const ViewAllButton = styled(HeartGamesButton).attrs({ variant: 'outline' })`
  font-size: 0.8rem;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    padding: ${theme.spacing.sm} ${theme.spacing.md};
  }
`;

// Card Components
export const GameCard = styled.div`
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
    border-color: ${theme.colors.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg};
  }
`;

export const GameCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
`;

export const GameCardIcon = styled.div<{ color: string }>`
  font-size: 2rem;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: ${props => props.color}20;
  border-radius: ${theme.borderRadius.md};
`;

export const GameCardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
  flex: 1;
  margin-left: ${theme.spacing.sm};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const GameCardDescription = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  margin-bottom: ${theme.spacing.sm};
  line-height: 1.5;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

export const GameCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.875rem;
  }
`;

export const GameCardBadge = styled.span<{ color: string }>`
  background: ${props => props.color}20;
  color: ${props => props.color};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.875rem;
  }
`;

export const GameCardActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: ${theme.spacing.md};

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: ${theme.spacing.xs};
    margin-top: ${theme.spacing.lg};
  }
`;

// Loading and Error States
export const HeartGamesLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
`;

export const HeartGamesError = styled.div`
  background: transparent;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md};
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

export const HeartGamesEmpty = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl};
  color: ${theme.colors.text.secondary};

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: ${theme.colors.text.primary};
    margin: 0 0 ${theme.spacing.sm} 0;
  }

  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

// Grid Layouts
export const GamesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${theme.spacing.md};
  padding: 0 ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: ${theme.spacing.sm};
    padding: 0 ${theme.spacing.md};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 ${theme.spacing.sm};
    grid-template-columns: 1fr;
    gap: ${theme.spacing.sm};
  }
`;

export const GamesPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

// Animation Components
export const HeartGamesFadeIn = styled.div`
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const HeartGamesSlideIn = styled.div`
  animation: slideIn 0.3s ease-in-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// Utility Components
export const HeartGamesDivider = styled.div`
  height: 1px;
  background: ${theme.colors.border};
  margin: ${theme.spacing.md} 0;
`;

export const HeartGamesSpacer = styled.div<{ size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' }>`
  height: ${props => {
    switch (props.size) {
      case 'xs': return theme.spacing.xs;
      case 'sm': return theme.spacing.sm;
      case 'md': return theme.spacing.md;
      case 'lg': return theme.spacing.lg;
      case 'xl': return theme.spacing.xl;
      default: return theme.spacing.md;
    }
  }};
`;
